import React, { useState, useEffect } from 'react';
import { Address, AddressFormData, CreateAddressRequest, UpdateAddressRequest, ComponentValidation } from './types';
import { FormErrors } from '../../../types';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import { useAddresses } from './hooks/useAddresses';
import { validateAddress } from '../../../utils/validation';
import AddressFormSection from './AddressFormSection';
import { ActionButton } from '../../../components/common/ActionButton';
import '../../../components/common/CRMForm.css';

interface AddressFormProps {
  address: Address | null;
  onSave: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const AddressForm: React.FC<AddressFormProps> = ({ address, onSave, onCancel, onDirtyChange }) => {
  const isEditing = !!address;
  const { createAddress, updateAddress } = useAddresses();
  
  // Form state
  const [formData, setFormData] = useState<AddressFormData>({
    street1: '',
    street2: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isValidated, setIsValidated] = useState(false); // Track if address was validated via Google
  const [componentValidation, setComponentValidation] = useState<ComponentValidation | null>(null); // Track component validation

  // Initialize form data
  useEffect(() => {
    if (address) {
      setFormData({
        street1: address.street1,
        street2: address.street2 || '',
        unit: address.unit || '',
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country
      });
    } else {
      // Reset form for new address
      setFormData({
        street1: '',
        street2: '',
        unit: '',
        city: '',
        state: '',
        zip: '',
        country: 'US'
      });
    }
      setErrors({});
      setHasChanges(false);
      setIsValidated(address?.verified || false);
    }, [address]);

  // Track changes by comparing current form data with original address data
  useEffect(() => {
    if (isEditing && address) {
      const hasAddressChanges = 
        formData.street1 !== address.street1 ||
        formData.street2 !== (address.street2 || '') ||
        formData.unit !== (address.unit || '') ||
        formData.city !== address.city ||
        formData.state !== address.state ||
        formData.zip !== address.zip ||
        formData.country !== address.country;
      
      setHasChanges(hasAddressChanges);
    } else if (!isEditing) {
      // For new addresses, check if any data has been entered
      const hasAnyData = 
        formData.street1.trim() !== '' ||
        formData.street2.trim() !== '' ||
        formData.unit.trim() !== '' ||
        formData.city.trim() !== '' ||
        formData.state.trim() !== '' ||
        formData.zip.trim() !== '' ||
        formData.country.trim() !== '';
      
      setHasChanges(hasAnyData);
    }
  }, [formData, address, isEditing]);

  // Track unsaved changes
  useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate address - validateAddress returns errors with 'address.' prefix
    const addressErrors = validateAddress(formData);
    Object.assign(newErrors, addressErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

      const handleInputChange = (field: keyof AddressFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
        
        // Reset validated status and component validation if user manually changes address fields
        if (isValidated) {
          setIsValidated(false);
          setComponentValidation(null);
        }
        
        // Clear errors for changed fields
        if (errors[field as string]) {
          setErrors(prev => ({ ...prev, [field as string]: '' }));
        }
      };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditing && address) {
        // Update existing address
        const updateData: UpdateAddressRequest = {
          street1: formData.street1,
          street2: formData.street2 || undefined,
          unit: formData.unit || undefined,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          verified: isValidated ? true : undefined, // Only set verified if address was validated
          verified_components: componentValidation ? JSON.stringify(componentValidation) : null // Save component validation as JSON
        };
        await updateAddress(address.id, updateData);
      } else {
        // Create new address
        const createData: CreateAddressRequest = {
          street1: formData.street1,
          street2: formData.street2 || undefined,
          unit: formData.unit || undefined,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          verified: isValidated, // Set verified if address was validated
          verified_components: componentValidation ? JSON.stringify(componentValidation) : null // Save component validation as JSON
        };
        await createAddress(createData);
      }
      
      setHasChanges(false);
      onSave();
    } catch (error: any) {
      console.error('Error saving address:', error);
      // Extract error message from API response
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to save address. Please try again.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    onCancel();
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Address' : 'Create Address'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      {errors.general && (
        <div className="app-error-message">{errors.general}</div>
      )}

      <div className="form-content">
        {/* Address Section */}
        <section className="form-section">
          <h3>Address Information</h3>
          <AddressFormSection
            addressData={formData}
            errors={errors}
            onChange={handleInputChange}
            verified={isValidated}
            onValidated={(validation) => {
              setIsValidated(true);
              setComponentValidation(validation);
            }}
          />
        </section>
      </div>

      <div className="app-form-actions">
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <ActionButton
          className="app-btn app-btn-primary"
          label={isEditing ? 'Update' : 'Create'}
          busyLabel="Saving..."
          busy={loading}
          disabled={!hasChanges}
          onClick={() => handleSave()}
        />
      </div>
    </div>
  );
};

export default AddressForm;

